import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import Button from '../ui/Button';
import { Card } from '../ui/Card';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Textarea from '../ui/Textarea';
import Modal from '../ui/Modal';
import Badge from '../ui/Badge';
import BrandContextBanner from '../shared/BrandContextBanner';
import VariationControls from '../shared/VariationControls';
import { blogService, BlogConnection, ContentSchedule, BlogPost } from '../../services/blogService';
import { Sparkles, Wand2, RefreshCw, Calendar, Send, Trash2, Edit3, Plus, Play, Pause, Loader2, Clock, PenSquare } from 'lucide-react';
import AiCostBadge from '../ai/AiCostBadge';

// ==================== Types & constants ====================

type KindKey = 'article' | 'news' | 'tutorial' | 'listicle' | 'review';
type ScheduleMode = 'draft' | 'now' | 'schedule';
type FrequencyKey = 'daily' | 'weekly' | 'biweekly' | 'monthly';
type BadgeVariant = 'primary' | 'success' | 'warning' | 'danger' | 'gray';

const TONE_VALUES = ['professional', 'casual', 'friendly', 'humorous', 'inspirational', 'formal', 'bold', 'educational'] as const;

// ==================== Helpers ====================

function todayISODate(): string {
  const d = new Date();
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function parseTopics(raw: string): string[] {
  return raw
    .split('\n')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

function formatDateTime(value?: string | null): string {
  if (!value) return '—';
  const d = new Date(value);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('pt-BR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function stripHtml(html?: string): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function seoPillClasses(score?: number): string {
  const s = score ?? 0;
  if (s >= 80) return 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-300';
  if (s >= 50) return 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/40 dark:text-yellow-300';
  return 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300';
}

function buildScheduleDates(startDate: string, time: string, spacing: number, count: number): string[] {
  const dates: string[] = [];
  const parts = (time || '09:00').split(':');
  const h = parseInt(parts[0], 10);
  const m = parseInt(parts[1], 10);
  const base = new Date(`${startDate}T00:00:00`);
  if (isNaN(base.getTime())) return dates;
  const safeSpacing = spacing > 0 ? spacing : 1;
  for (let i = 0; i < count; i++) {
    const d = new Date(base);
    d.setDate(base.getDate() + i * safeSpacing);
    d.setHours(isNaN(h) ? 9 : h, isNaN(m) ? 0 : m, 0, 0);
    dates.push(d.toISOString());
  }
  return dates;
}

function toLocalInputValue(value?: string | null): string {
  const d = value ? new Date(value) : new Date();
  if (isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

// ==================== Small presentational pieces ====================

interface ChipProps {
  active: boolean;
  onClick: () => void;
  icon?: React.ReactNode;
  children: React.ReactNode;
}

const Chip: React.FC<ChipProps> = ({ active, onClick, icon, children }) => (
  <button
    type="button"
    onClick={onClick}
    className={
      'inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-medium border transition-all duration-200 ' +
      (active
        ? 'bg-gradient-to-r from-primary-600 to-purple-600 text-white border-transparent shadow-md'
        : 'bg-white dark:bg-gray-800/50 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-primary-400 dark:hover:border-primary-500')
    }
  >
    {icon}
    {children}
  </button>
);

interface ToggleProps {
  checked: boolean;
  onChange: () => void;
}

const Toggle: React.FC<ToggleProps> = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={onChange}
    aria-pressed={checked}
    className={
      'relative inline-flex h-6 w-11 flex-shrink-0 rounded-full transition-colors duration-200 ' +
      (checked ? 'bg-primary-600' : 'bg-gray-300 dark:bg-gray-600')
    }
  >
    <span
      className={
        'inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform duration-200 mt-0.5 ' +
        (checked ? 'translate-x-5' : 'translate-x-0.5')
      }
    />
  </button>
);

interface CountPillsProps {
  value: number;
  onChange: (n: number) => void;
  max: number;
}

const CountPills: React.FC<CountPillsProps> = ({ value, onChange, max }) => (
  <div className="flex flex-wrap gap-2">
    {Array.from({ length: max }, (_, i) => i + 1).map((n) => (
      <button
        key={n}
        type="button"
        onClick={() => onChange(n)}
        className={
          'w-10 h-10 rounded-lg text-sm font-semibold border transition-all duration-200 ' +
          (value === n
            ? 'bg-gradient-to-r from-primary-600 to-purple-600 text-white border-transparent shadow-md'
            : 'bg-white dark:bg-gray-800/50 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-primary-400 dark:hover:border-primary-500')
        }
      >
        {n}
      </button>
    ))}
  </div>
);

// ==================== Schedule form state ====================

interface ScheduleForm {
  name: string;
  kind: KindKey;
  frequency: FrequencyKey;
  days_of_week: number[];
  time_of_day: string;
  posts_per_run: number;
  topics: string;
  tone: string;
  blog_connection_id: string;
  ignore_pattern: boolean;
  variation_direction: string;
  auto_publish: boolean;
}

const emptyScheduleForm: ScheduleForm = {
  name: '',
  kind: 'article',
  frequency: 'weekly',
  days_of_week: [1],
  time_of_day: '09:00',
  posts_per_run: 7,
  topics: '',
  tone: 'professional',
  blog_connection_id: '',
  ignore_pattern: false,
  variation_direction: '',
  auto_publish: false,
};

// ==================== Component ====================

const ContentEngine: React.FC = () => {
  const { t } = useTranslation();

  // ----- localized option lists -----
  const KINDS: { key: KindKey; label: string }[] = [
    { key: 'article', label: t('blogSeo.engine.kindArticle', 'Artigo') },
    { key: 'news', label: t('blogSeo.engine.kindNews', 'Notícia') },
    { key: 'tutorial', label: t('blogSeo.engine.kindTutorial', 'Tutorial') },
    { key: 'listicle', label: t('blogSeo.engine.kindListicle', 'Lista') },
    { key: 'review', label: t('blogSeo.engine.kindReview', 'Review') },
  ];
  const KIND_LABELS: Record<string, string> = {
    article: t('blogSeo.engine.kindArticle', 'Artigo'),
    news: t('blogSeo.engine.kindNews', 'Notícia'),
    tutorial: t('blogSeo.engine.kindTutorial', 'Tutorial'),
    listicle: t('blogSeo.engine.kindListicle', 'Lista'),
    review: t('blogSeo.engine.kindReview', 'Review'),
  };
  const TONE_OPTIONS = [
    { value: 'professional', label: t('blogSeo.engine.toneProfessional', 'Profissional') },
    { value: 'casual', label: t('blogSeo.engine.toneCasual', 'Casual') },
    { value: 'friendly', label: t('blogSeo.engine.toneFriendly', 'Amigável') },
    { value: 'humorous', label: t('blogSeo.engine.toneHumorous', 'Humor') },
    { value: 'inspirational', label: t('blogSeo.engine.toneInspirational', 'Inspirador') },
    { value: 'formal', label: t('blogSeo.engine.toneFormal', 'Formal') },
    { value: 'bold', label: t('blogSeo.engine.toneBold', 'Ousado') },
    { value: 'educational', label: t('blogSeo.engine.toneEducational', 'Educativo') },
  ];
  void TONE_VALUES;
  const FREQUENCY_OPTIONS = [
    { value: 'daily', label: t('blogSeo.engine.frequencyDaily', 'Diário') },
    { value: 'weekly', label: t('blogSeo.engine.frequencyWeekly', 'Semanal') },
    { value: 'biweekly', label: t('blogSeo.engine.frequencyBiweekly', 'Quinzenal') },
    { value: 'monthly', label: t('blogSeo.engine.frequencyMonthly', 'Mensal') },
  ];
  const FREQUENCY_LABELS: Record<string, string> = {
    daily: t('blogSeo.engine.frequencyDaily', 'Diário'),
    weekly: t('blogSeo.engine.frequencyWeekly', 'Semanal'),
    biweekly: t('blogSeo.engine.frequencyBiweekly', 'Quinzenal'),
    monthly: t('blogSeo.engine.frequencyMonthly', 'Mensal'),
  };
  const WEEKDAYS = [
    { value: 0, label: t('blogSeo.engine.weekdaySun', 'Dom') },
    { value: 1, label: t('blogSeo.engine.weekdayMon', 'Seg') },
    { value: 2, label: t('blogSeo.engine.weekdayTue', 'Ter') },
    { value: 3, label: t('blogSeo.engine.weekdayWed', 'Qua') },
    { value: 4, label: t('blogSeo.engine.weekdayThu', 'Qui') },
    { value: 5, label: t('blogSeo.engine.weekdayFri', 'Sex') },
    { value: 6, label: t('blogSeo.engine.weekdaySat', 'Sáb') },
  ];
  const SCHEDULE_MODES: { mode: ScheduleMode; label: string; hint: string }[] = [
    { mode: 'draft', label: t('blogSeo.engine.modeDraftLabel', 'Salvar como rascunho'), hint: t('blogSeo.engine.modeDraftHint', 'Revise antes de publicar') },
    { mode: 'now', label: t('blogSeo.engine.modeNowLabel', 'Publicar/enfileirar agora'), hint: t('blogSeo.engine.modeNowHint', 'Requer uma conexão') },
    { mode: 'schedule', label: t('blogSeo.engine.modeScheduleLabel', 'Agendar'), hint: t('blogSeo.engine.modeScheduleHint', 'Distribuir ao longo do tempo') },
  ];

  // ----- shared data -----
  const [connections, setConnections] = useState<BlogConnection[]>([]);

  // ----- Section 1: instant generator -----
  const [kind, setKind] = useState<KindKey>('article');
  const [count, setCount] = useState<number>(3);
  const [tone, setTone] = useState<string>('professional');
  const [ignorePattern, setIgnorePattern] = useState<boolean>(false);
  const [variationDirection, setVariationDirection] = useState<string>('');
  const [topicsText, setTopicsText] = useState<string>('');
  const [connectionId, setConnectionId] = useState<string>('');
  const [scheduleMode, setScheduleMode] = useState<ScheduleMode>('draft');
  const [startDate, setStartDate] = useState<string>(todayISODate());
  const [startTime, setStartTime] = useState<string>('09:00');
  const [spacing, setSpacing] = useState<number>(1);
  const [generating, setGenerating] = useState<boolean>(false);

  // ----- suggestions -----
  const [suggestions, setSuggestions] = useState<BlogPost[]>([]);
  const [busyCardId, setBusyCardId] = useState<string | null>(null);

  // quick-edit modal
  const [editPost, setEditPost] = useState<BlogPost | null>(null);
  const [editTitle, setEditTitle] = useState<string>('');
  const [editContent, setEditContent] = useState<string>('');
  const [savingEdit, setSavingEdit] = useState<boolean>(false);

  // schedule-card modal
  const [schedulePost, setSchedulePost] = useState<BlogPost | null>(null);
  const [scheduleAt, setScheduleAt] = useState<string>('');
  const [scheduleConnId, setScheduleConnId] = useState<string>('');
  const [savingCardSchedule, setSavingCardSchedule] = useState<boolean>(false);

  // ----- Section 2: schedules -----
  const [schedules, setSchedules] = useState<ContentSchedule[]>([]);
  const [loadingSchedules, setLoadingSchedules] = useState<boolean>(true);
  const [modalOpen, setModalOpen] = useState<boolean>(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<ScheduleForm>(emptyScheduleForm);
  const [savingSchedule, setSavingSchedule] = useState<boolean>(false);
  const [runningId, setRunningId] = useState<string | null>(null);

  // ==================== Effects ====================

  useEffect(() => {
    void loadConnections();
    void loadSchedules();
  }, []);

  const loadConnections = async () => {
    try {
      const res = await blogService.listConnections();
      setConnections(res?.data ?? []);
    } catch {
      // silencioso: conexões são opcionais para o gerador
    }
  };

  const loadSchedules = async () => {
    setLoadingSchedules(true);
    try {
      const res = await blogService.listSchedules();
      // Blog area shows ONLY blog/news routines — social routines live in the Social area.
      const all = res?.data ?? [];
      setSchedules(all.filter((s) => !(s.channels || []).includes('social')));
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? err?.message ?? t('blogSeo.engine.errLoadSchedules', 'Erro ao carregar rotinas'));
    } finally {
      setLoadingSchedules(false);
    }
  };

  // ==================== Derived ====================

  const connectionOptions: { value: string; label: string }[] = [
    { value: '', label: t('blogSeo.engine.connectionNone', 'Não publicar agora (rascunho)') },
    ...connections.map((c) => ({ value: c.id, label: c.name })),
  ];

  // ==================== Section 1: generate ====================

  const handleGenerate = async () => {
    if (count < 1) {
      toast.error(t('blogSeo.engine.errMinCount', 'A quantidade deve ser pelo menos 1.'));
      return;
    }
    if (scheduleMode === 'now' && !connectionId) {
      toast.error(t('blogSeo.engine.errPublishNeedConnection', 'Para publicar agora, escolha uma conexão de destino.'));
      return;
    }

    const topics = parseTopics(topicsText);

    let schedule: { mode: ScheduleMode; dates?: string[] };
    if (scheduleMode === 'schedule') {
      const dates = buildScheduleDates(startDate, startTime, spacing, count);
      if (dates.length === 0) {
        toast.error(t('blogSeo.engine.errInvalidStartDate', 'Data de início inválida.'));
        return;
      }
      schedule = { mode: 'schedule', dates };
    } else {
      schedule = { mode: scheduleMode };
    }

    setGenerating(true);
    try {
      const res = await blogService.generate({
        channels: ['blog'],
        kind,
        count,
        topics: topics.length > 0 ? topics : undefined,
        tone,
        connection_id: connectionId || null,
        schedule,
        ignore_pattern: ignorePattern,
        variation_direction: ignorePattern && variationDirection.trim() ? variationDirection.trim() : undefined,
      });
      const data = res?.data;
      const blogPosts = data?.blogPosts ?? [];
      const blogCount = data?.blogCount ?? blogPosts.length;
      setSuggestions((prev) => [...blogPosts, ...prev]);
      toast.success(t('blogSeo.engine.successGenerated', '{{count}} conteúdos gerados!', { count: blogCount }));
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? err?.message ?? t('blogSeo.engine.errGenerate', 'Erro ao gerar'));
    } finally {
      setGenerating(false);
    }
  };

  // ==================== Suggestion card actions ====================

  const replaceSuggestion = (post: BlogPost) =>
    setSuggestions((prev) => prev.map((p) => (p.id === post.id ? post : p)));

  const removeSuggestion = (id: string) =>
    setSuggestions((prev) => prev.filter((p) => p.id !== id));

  const handleRegenerate = async (post: BlogPost) => {
    setBusyCardId(post.id);
    try {
      const res = await blogService.regeneratePost(post.id, tone);
      if (res?.data) replaceSuggestion(res.data);
      toast.success(t('blogSeo.engine.successRegenerated', 'Conteúdo regenerado!'));
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? err?.message ?? t('blogSeo.engine.errRegenerate', 'Erro ao regenerar'));
    } finally {
      setBusyCardId(null);
    }
  };

  const handlePublishCard = async (post: BlogPost) => {
    const conn = post.connection_id ?? connectionId;
    if (!conn) {
      toast.error(t('blogSeo.engine.errPickConnectionPublish', 'Escolha uma conexão de destino antes de publicar.'));
      return;
    }
    setBusyCardId(post.id);
    try {
      await blogService.publishPost(post.id, conn);
      replaceSuggestion({ ...post, status: 'published', connection_id: conn });
      toast.success(t('blogSeo.engine.successPublished', 'Conteúdo publicado!'));
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? err?.message ?? t('blogSeo.engine.errPublish', 'Erro ao publicar'));
    } finally {
      setBusyCardId(null);
    }
  };

  const handleDiscardCard = async (post: BlogPost) => {
    setBusyCardId(post.id);
    try {
      await blogService.deletePost(post.id);
      removeSuggestion(post.id);
      toast.success(t('blogSeo.engine.successDiscarded', 'Conteúdo descartado.'));
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? err?.message ?? t('blogSeo.engine.errDiscard', 'Erro ao descartar'));
    } finally {
      setBusyCardId(null);
    }
  };

  // ----- quick edit modal -----
  const openEditCard = (post: BlogPost) => {
    setEditPost(post);
    setEditTitle(post.title ?? '');
    setEditContent(post.content ?? '');
  };

  const handleSaveEdit = async () => {
    if (!editPost) return;
    setSavingEdit(true);
    try {
      const res = await blogService.updatePost(editPost.id, { title: editTitle, content: editContent });
      if (res?.data) replaceSuggestion(res.data);
      toast.success(t('blogSeo.engine.successSaved', 'Alterações salvas!'));
      setEditPost(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? err?.message ?? t('blogSeo.engine.errSave', 'Erro ao salvar'));
    } finally {
      setSavingEdit(false);
    }
  };

  // ----- schedule card modal -----
  const openScheduleCard = (post: BlogPost) => {
    setSchedulePost(post);
    setScheduleAt(toLocalInputValue(post.scheduled_at));
    setScheduleConnId(post.connection_id ?? connectionId ?? '');
  };

  const handleSaveCardSchedule = async () => {
    if (!schedulePost) return;
    if (!scheduleConnId) {
      toast.error(t('blogSeo.engine.errPickConnectionSchedule', 'Escolha uma conexão de destino para agendar.'));
      return;
    }
    if (!scheduleAt) {
      toast.error(t('blogSeo.engine.errPickDateTime', 'Escolha uma data e horário.'));
      return;
    }
    setSavingCardSchedule(true);
    try {
      const iso = new Date(scheduleAt).toISOString();
      const res = await blogService.updatePost(schedulePost.id, {
        status: 'scheduled',
        scheduled_at: iso,
        connection_id: scheduleConnId,
      });
      if (res?.data) replaceSuggestion(res.data);
      toast.success(t('blogSeo.engine.successScheduled', 'Conteúdo agendado!'));
      setSchedulePost(null);
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? err?.message ?? t('blogSeo.engine.errSchedule', 'Erro ao agendar'));
    } finally {
      setSavingCardSchedule(false);
    }
  };

  // ==================== Section 2: schedule CRUD ====================

  const openCreateModal = () => {
    setEditingId(null);
    setForm(emptyScheduleForm);
    setModalOpen(true);
  };

  const openEditModal = (s: ContentSchedule) => {
    setEditingId(s.id);
    setForm({
      name: s.name ?? '',
      kind: (s.metadata?.kind as KindKey) ?? 'article',
      frequency: s.frequency ?? 'weekly',
      days_of_week: s.days_of_week ?? [],
      time_of_day: s.time_of_day ?? '09:00',
      posts_per_run: s.posts_per_run ?? 1,
      topics: (s.topics ?? []).join('\n'),
      tone: s.tone ?? 'professional',
      blog_connection_id: s.blog_connection_id ?? '',
      ignore_pattern: s.ignore_pattern ?? s.metadata?.ignore_pattern ?? false,
      variation_direction: s.variation_direction ?? s.metadata?.variation_direction ?? '',
      auto_publish: s.auto_publish ?? false,
    });
    setModalOpen(true);
  };

  const updateForm = <K extends keyof ScheduleForm>(key: K, value: ScheduleForm[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const toggleFormWeekday = (value: number) =>
    setForm((prev) => ({
      ...prev,
      days_of_week: prev.days_of_week.includes(value)
        ? prev.days_of_week.filter((d) => d !== value)
        : [...prev.days_of_week, value].sort((a, b) => a - b),
    }));

  const showFormWeekdays = form.frequency === 'weekly' || form.frequency === 'biweekly';

  const handleSaveSchedule = async () => {
    if (!form.name.trim()) {
      toast.error(t('blogSeo.engine.errNameRoutine', 'Dê um nome para a rotina.'));
      return;
    }
    if (showFormWeekdays && form.days_of_week.length === 0) {
      toast.error(t('blogSeo.engine.errSelectWeekday', 'Selecione pelo menos um dia da semana.'));
      return;
    }

    const payload: Partial<ContentSchedule> = {
      name: form.name.trim(),
      channels: ['blog'],
      kind: form.kind,
      frequency: form.frequency,
      days_of_week: showFormWeekdays ? form.days_of_week : [],
      time_of_day: form.time_of_day,
      posts_per_run: form.posts_per_run,
      topics: parseTopics(form.topics),
      tone: form.tone || null,
      blog_connection_id: form.blog_connection_id || null,
      ignore_pattern: form.ignore_pattern,
      variation_direction: form.ignore_pattern && form.variation_direction.trim() ? form.variation_direction.trim() : undefined,
      auto_publish: form.auto_publish,
    };

    setSavingSchedule(true);
    try {
      if (editingId) {
        await blogService.updateSchedule(editingId, payload);
        toast.success(t('blogSeo.engine.successRoutineUpdated', 'Rotina atualizada!'));
      } else {
        await blogService.createSchedule(payload);
        toast.success(t('blogSeo.engine.successRoutineCreated', 'Rotina criada!'));
      }
      setModalOpen(false);
      await loadSchedules();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? err?.message ?? t('blogSeo.engine.errSaveRoutine', 'Erro ao salvar rotina'));
    } finally {
      setSavingSchedule(false);
    }
  };

  const handleToggleStatus = async (s: ContentSchedule) => {
    const nextStatus: ContentSchedule['status'] = s.status === 'active' ? 'paused' : 'active';
    try {
      await blogService.updateSchedule(s.id, { status: nextStatus });
      toast.success(nextStatus === 'active' ? t('blogSeo.engine.successRoutineActivated', 'Rotina ativada!') : t('blogSeo.engine.successRoutinePaused', 'Rotina pausada!'));
      await loadSchedules();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? err?.message ?? t('blogSeo.engine.errUpdateStatus', 'Erro ao atualizar status'));
    }
  };

  const handleRunNow = async (s: ContentSchedule) => {
    setRunningId(s.id);
    try {
      const res = await blogService.runScheduleNow(s.id);
      const data = res?.data ?? res;
      const blogCount = data?.blogCount ?? 0;
      toast.success(t('blogSeo.engine.successRoutineRan', 'Rotina executada: {{count}} conteúdos gerados.', { count: blogCount }));
      await loadSchedules();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? err?.message ?? t('blogSeo.engine.errRunRoutine', 'Erro ao executar rotina'));
    } finally {
      setRunningId(null);
    }
  };

  const handleDeleteSchedule = async (s: ContentSchedule) => {
    if (!window.confirm(t('blogSeo.engine.confirmDeleteRoutine', 'Excluir a rotina "{{name}}"? Esta ação não pode ser desfeita.', { name: s.name }))) return;
    try {
      await blogService.deleteSchedule(s.id);
      toast.success(t('blogSeo.engine.successRoutineDeleted', 'Rotina excluída.'));
      await loadSchedules();
    } catch (err: any) {
      toast.error(err?.response?.data?.message ?? err?.message ?? t('blogSeo.engine.errDeleteRoutine', 'Erro ao excluir rotina'));
    }
  };

  // ==================== Render helpers ====================

  const kindBadgeVariant = (k?: string): BadgeVariant =>
    k === 'news' ? 'warning' : k === 'tutorial' ? 'success' : k === 'listicle' ? 'primary' : k === 'review' ? 'gray' : 'primary';

  const kindLabel = (k?: string): string => KIND_LABELS[k ?? 'article'] ?? t('blogSeo.engine.kindArticle', 'Artigo');

  // ==================== JSX ====================

  return (
    <div className="space-y-8">
      {/* ============ HERO ============ */}
      <div className="bg-gradient-to-br from-primary-600 via-purple-600 to-indigo-700 rounded-2xl p-6 text-white shadow-lg">
        <div className="flex items-center gap-3">
          <div className="p-2.5 rounded-xl bg-white/15 backdrop-blur-sm relative">
            <Wand2 className="w-6 h-6" />
            <Sparkles className="w-3.5 h-3.5 absolute -top-1 -right-1 text-yellow-200" />
          </div>
          <div>
            <h2 className="text-2xl font-bold leading-tight">{t('blogSeo.engine.heroTitle', 'Gerador de conteúdo com IA')}</h2>
            <p className="text-white/80 text-sm mt-0.5">
              {t('blogSeo.engine.heroSubtitle', 'Gere artigos, notícias e mais para o seu blog — no seu tom, seguindo seu padrão.')}
            </p>
          </div>
        </div>
      </div>

      {/* ============ BRAND CONTEXT BANNER ============ */}
      <BrandContextBanner ignorePattern={ignorePattern} />

      {/* ============ SECTION 1: Instant generator ============ */}
      <Card className="flex flex-col gap-6">
        <div className="flex items-center gap-2">
          <Wand2 className="w-5 h-5 text-primary-600 dark:text-primary-400" />
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('blogSeo.engine.instantGeneratorTitle', 'Gerador instantâneo')}</h3>
        </div>

        {/* Content type */}
        <div>
          <label className="label">{t('blogSeo.engine.typeLabel', 'Tipo de conteúdo')}</label>
          <div className="flex flex-wrap gap-2.5">
            {KINDS.map((k) => (
              <Chip key={k.key} active={kind === k.key} onClick={() => setKind(k.key)}>
                {k.label}
              </Chip>
            ))}
          </div>
        </div>

        {/* Quantity (pills) + tone */}
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="label">{t('blogSeo.engine.quantityLabel', 'Quantidade')}</label>
            <CountPills value={count} onChange={setCount} max={6} />
          </div>
          <Select label={t('blogSeo.engine.toneLabel', 'Tom de voz')} options={TONE_OPTIONS} value={tone} onChange={(e) => setTone(e.target.value)} />
        </div>

        {/* Ignore pattern + variation direction */}
        <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40 p-4">
          <VariationControls
            ignorePattern={ignorePattern}
            onIgnoreChange={setIgnorePattern}
            variationDirection={variationDirection}
            onVariationChange={setVariationDirection}
          />
        </div>

        {/* Topics */}
        <Textarea
          label={t('blogSeo.engine.topicsLabel', 'Temas (um por linha)')}
          value={topicsText}
          onChange={(e) => setTopicsText(e.target.value)}
          placeholder={t('blogSeo.engine.topicsPlaceholder', 'Deixe vazio para a IA sugerir a partir do seu negócio')}
        />

        {/* Blog connection */}
        <Select
          label={t('blogSeo.engine.publishTargetLabel', 'Destino de publicação')}
          options={connectionOptions}
          value={connectionId}
          onChange={(e) => setConnectionId(e.target.value)}
        />

        {/* Scheduling mode */}
        <div>
          <label className="label">{t('blogSeo.engine.whatToDoLabel', 'O que fazer com o conteúdo gerado?')}</label>
          <div className="grid sm:grid-cols-3 gap-2.5">
            {SCHEDULE_MODES.map((opt) => (
              <button
                key={opt.mode}
                type="button"
                onClick={() => setScheduleMode(opt.mode)}
                className={
                  'px-4 py-3 rounded-xl text-sm font-medium border transition-all duration-200 text-center ' +
                  (scheduleMode === opt.mode
                    ? 'bg-gradient-to-r from-primary-600 to-purple-600 text-white border-transparent shadow-md'
                    : 'bg-white dark:bg-gray-800/50 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-primary-400 dark:hover:border-primary-500')
                }
              >
                <span className="block">{opt.label}</span>
                <span className={'block text-xs mt-0.5 ' + (scheduleMode === opt.mode ? 'text-white/80' : 'text-gray-400 dark:text-gray-500')}>
                  {opt.hint}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Schedule details */}
        {scheduleMode === 'schedule' && (
          <div className="grid md:grid-cols-3 gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40">
            <Input label={t('blogSeo.engine.startDateLabel', 'Data de início')} type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} />
            <Input label={t('blogSeo.engine.timeLabel', 'Horário')} type="time" value={startTime} onChange={(e) => setStartTime(e.target.value)} />
            <Input
              label={t('blogSeo.engine.spacingLabel', 'Espaçamento (dias)')}
              type="number"
              min={1}
              value={spacing}
              onChange={(e) => setSpacing(Math.max(1, Number(e.target.value) || 1))}
            />
          </div>
        )}

        {/* Generate button */}
        <div className="flex flex-col items-end gap-1">
          <Button variant="primary" size="lg" isLoading={generating} leftIcon={<Wand2 className="w-4 h-4" />} onClick={handleGenerate}>
            {t('blogSeo.engine.generateButton', 'Gerar conteúdo')}
          </Button>
          <AiCostBadge feature="ai-generation" action="blog-generate" className="justify-end" />
        </div>

        {/* Loading banner */}
        {generating && (
          <div className="rounded-xl border border-primary-200 dark:border-primary-800 bg-gradient-to-r from-primary-50 to-purple-50 dark:from-primary-900/20 dark:to-purple-900/20 p-5 animate-pulse">
            <div className="flex items-center gap-3">
              <Loader2 className="w-5 h-5 text-primary-600 dark:text-primary-400 animate-spin" />
              <div>
                <p className="font-semibold text-gray-900 dark:text-white">{t('blogSeo.engine.aiWriting', 'A IA está criando seu conteúdo...')}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">{t('blogSeo.engine.aiWritingHint', '(pode levar alguns minutos)')}</p>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* ============ Suggestions grid ============ */}
      {suggestions.length > 0 && (
        <div className="flex flex-col gap-4">
          <div>
            <div className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary-600 dark:text-primary-400" />
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('blogSeo.engine.generatedNowTitle', 'Gerados agora')}</h3>
              <Badge variant="primary">{suggestions.length}</Badge>
            </div>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {t('blogSeo.engine.generatedNowHint', 'Já salvos como rascunho — gerencie em Posts (e Redes Sociais).')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {suggestions.map((post) => {
              const busy = busyCardId === post.id;
              const preview = post.excerpt?.trim() || stripHtml(post.content);
              return (
                <div key={post.id} className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800/50 p-4 flex flex-col gap-3">
                  {/* Top row */}
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex items-center gap-1.5">
                      <Badge variant={kindBadgeVariant(post.kind)}>{kindLabel(post.kind)}</Badge>
                      <Badge variant="success">{t('blogSeo.engine.badgeNew', 'NOVA')}</Badge>
                    </div>
                    <span
                      className={'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ' + seoPillClasses(post.seo_score)}
                      title={t('blogSeo.engine.seoScoreTitle', 'Pontuação de SEO')}
                    >
                      {t('blogSeo.engine.seoLabel', 'SEO')} {post.seo_score ?? 0}
                    </span>
                  </div>

                  {/* Title */}
                  <h4 className="font-semibold text-gray-900 dark:text-white line-clamp-2">{post.title}</h4>

                  {/* Preview */}
                  <p className="text-sm text-gray-500 dark:text-gray-400 line-clamp-3">{preview || t('blogSeo.engine.noPreview', 'Sem prévia disponível.')}</p>

                  {/* Meta */}
                  <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-500 dark:text-gray-400 mt-auto">
                    <span className="inline-flex items-center gap-1">
                      <PenSquare className="w-3.5 h-3.5" />
                      {t('blogSeo.engine.wordCount', '{{count}} palavras', { count: post.word_count ?? 0 })}
                    </span>
                    {post.scheduled_at && (
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3.5 h-3.5" />
                        {formatDateTime(post.scheduled_at)}
                      </span>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                    <Button variant="ghost" size="sm" leftIcon={<Edit3 className="w-4 h-4" />} onClick={() => openEditCard(post)}>
                      {t('blogSeo.engine.actionView', 'Ver')}
                    </Button>
                    <Button variant="ghost" size="sm" isLoading={busy} leftIcon={<RefreshCw className="w-4 h-4" />} onClick={() => handleRegenerate(post)}>
                      {t('blogSeo.engine.actionRegenerate', 'Regenerar')}
                    </Button>
                    <Button variant="ghost" size="sm" leftIcon={<Calendar className="w-4 h-4" />} onClick={() => openScheduleCard(post)}>
                      {t('blogSeo.engine.actionSchedule', 'Agendar')}
                    </Button>
                    <Button variant="ghost" size="sm" isLoading={busy} leftIcon={<Send className="w-4 h-4" />} onClick={() => handlePublishCard(post)}>
                      {t('blogSeo.engine.actionPublish', 'Publicar')}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      className="text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-900/20"
                      leftIcon={<Trash2 className="w-4 h-4" />}
                      onClick={() => handleDiscardCard(post)}
                    >
                      {t('blogSeo.engine.actionDiscard', 'Descartar')}
                    </Button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* ============ SECTION 2: Recurring ============ */}
      <div className="flex flex-col gap-5">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div className="flex items-center gap-2">
            <RefreshCw className="w-5 h-5 text-primary-600 dark:text-primary-400" />
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{t('blogSeo.engine.recurringTitle', 'Geração automática')}</h3>
              <p className="text-sm text-gray-500 dark:text-gray-400">
                {t('blogSeo.engine.recurringSubtitle', 'Crie uma rotina que gera conteúdo sozinha — ex.: toda segunda, gera os posts da semana.')}
              </p>
            </div>
          </div>
          <Button variant="primary" leftIcon={<Plus className="w-4 h-4" />} onClick={openCreateModal}>
            {t('blogSeo.engine.newRoutineButton', 'Nova rotina')}
          </Button>
        </div>

        {loadingSchedules ? (
          <Card className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
            <Loader2 className="w-5 h-5 animate-spin mr-2" />
            {t('blogSeo.engine.loadingRoutines', 'Carregando rotinas...')}
          </Card>
        ) : schedules.length === 0 ? (
          <Card className="flex flex-col items-center justify-center py-12 text-center">
            <div className="p-3 rounded-2xl bg-gray-100 dark:bg-gray-800 mb-4">
              <Calendar className="w-8 h-8 text-gray-400 dark:text-gray-500" />
            </div>
            <p className="font-medium text-gray-900 dark:text-white">{t('blogSeo.engine.emptyTitle', 'Nenhuma rotina ainda')}</p>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1 max-w-md">
              {t('blogSeo.engine.emptySubtitle', 'Crie uma rotina para gerar conteúdo automaticamente, sem precisar começar do zero toda vez.')}
            </p>
            <Button variant="secondary" className="mt-4" leftIcon={<Plus className="w-4 h-4" />} onClick={openCreateModal}>
              {t('blogSeo.engine.createFirstRoutine', 'Criar primeira rotina')}
            </Button>
          </Card>
        ) : (
          <div className="grid md:grid-cols-2 gap-4">
            {schedules.map((s) => (
              <Card key={s.id} className="flex flex-col gap-4">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0">
                    <h4 className="font-semibold text-gray-900 dark:text-white truncate">{s.name}</h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400">{FREQUENCY_LABELS[s.frequency] ?? s.frequency}</p>
                  </div>
                  <Badge variant={s.status === 'active' ? 'success' : 'gray'}>
                    {s.status === 'active' ? t('blogSeo.engine.statusActive', 'Ativo') : t('blogSeo.engine.statusPaused', 'Pausado')}
                  </Badge>
                </div>

                <div className="flex flex-wrap gap-1.5">
                  <Badge variant={kindBadgeVariant(s.metadata?.kind)}>
                    {kindLabel(s.metadata?.kind)}
                  </Badge>
                </div>

                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>{t('blogSeo.engine.nextRun', 'Próxima: {{date}}', { date: formatDateTime(s.next_run_at) })}</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300">
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span>{s.time_of_day}</span>
                  </div>
                  <div className="text-gray-600 dark:text-gray-300">
                    {t('blogSeo.engine.postsPerRunShort', '{{count}} posts por execução', { count: s.posts_per_run })}
                  </div>
                  <div className="text-gray-600 dark:text-gray-300">
                    {t('blogSeo.engine.runsCount', 'Execuções:')} <span className="font-medium">{s.runs_count ?? 0}</span>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-100 dark:border-gray-700">
                  <Button
                    variant="secondary"
                    size="sm"
                    leftIcon={s.status === 'active' ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                    onClick={() => handleToggleStatus(s)}
                  >
                    {s.status === 'active' ? t('blogSeo.engine.actionPause', 'Pausar') : t('blogSeo.engine.actionActivate', 'Ativar')}
                  </Button>
                  <Button variant="ghost" size="sm" isLoading={runningId === s.id} leftIcon={<Play className="w-4 h-4" />} onClick={() => handleRunNow(s)}>
                    {t('blogSeo.engine.actionRunNow', 'Rodar agora')}
                  </Button>
                  <Button variant="ghost" size="sm" leftIcon={<Edit3 className="w-4 h-4" />} onClick={() => openEditModal(s)}>
                    {t('blogSeo.engine.actionEdit', 'Editar')}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="text-red-600 hover:bg-red-50 hover:text-red-700 dark:hover:bg-red-900/20"
                    leftIcon={<Trash2 className="w-4 h-4" />}
                    onClick={() => handleDeleteSchedule(s)}
                  >
                    {t('blogSeo.engine.actionDelete', 'Excluir')}
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* ============ Quick-edit modal ============ */}
      <Modal isOpen={editPost !== null} onClose={() => setEditPost(null)} title={t('blogSeo.engine.reviewContentTitle', 'Revisar conteúdo')} size="2xl" maxHeight>
        <div className="flex flex-col gap-4">
          <Input label={t('blogSeo.engine.titleLabel', 'Título')} value={editTitle} onChange={(e) => setEditTitle(e.target.value)} />
          <Textarea label={t('blogSeo.engine.contentHtmlLabel', 'Conteúdo (HTML)')} value={editContent} onChange={(e) => setEditContent(e.target.value)} rows={14} />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => setEditPost(null)}>
              {t('blogSeo.engine.cancel', 'Cancelar')}
            </Button>
            <Button variant="primary" isLoading={savingEdit} onClick={handleSaveEdit}>
              {t('blogSeo.engine.save', 'Salvar')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ============ Schedule-card modal ============ */}
      <Modal isOpen={schedulePost !== null} onClose={() => setSchedulePost(null)} title={t('blogSeo.engine.schedulePublicationTitle', 'Agendar publicação')} size="md">
        <div className="flex flex-col gap-4">
          <Input label={t('blogSeo.engine.dateTimeLabel', 'Data e horário')} type="datetime-local" value={scheduleAt} onChange={(e) => setScheduleAt(e.target.value)} />
          <Select
            label={t('blogSeo.engine.publishTargetLabel', 'Destino de publicação')}
            options={connectionOptions}
            value={scheduleConnId}
            onChange={(e) => setScheduleConnId(e.target.value)}
          />
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => setSchedulePost(null)}>
              {t('blogSeo.engine.cancel', 'Cancelar')}
            </Button>
            <Button variant="primary" isLoading={savingCardSchedule} onClick={handleSaveCardSchedule}>
              {t('blogSeo.engine.actionSchedule', 'Agendar')}
            </Button>
          </div>
        </div>
      </Modal>

      {/* ============ Schedule routine modal ============ */}
      <Modal
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editingId ? t('blogSeo.engine.editRoutineTitle', 'Editar rotina') : t('blogSeo.engine.newRoutineTitle', 'Nova rotina')}
        size="2xl"
        maxHeight
      >
        <div className="flex flex-col gap-5">
          <Input
            label={t('blogSeo.engine.routineNameLabel', 'Nome da rotina')}
            value={form.name}
            onChange={(e) => updateForm('name', e.target.value)}
            placeholder={t('blogSeo.engine.routineNamePlaceholder', 'Ex.: Conteúdo semanal de skincare')}
          />

          {/* Content type */}
          <div>
            <label className="label">{t('blogSeo.engine.typeLabel', 'Tipo de conteúdo')}</label>
            <div className="flex flex-wrap gap-2.5">
              {KINDS.map((k) => (
                <Chip key={k.key} active={form.kind === k.key} onClick={() => updateForm('kind', k.key)}>
                  {k.label}
                </Chip>
              ))}
            </div>
          </div>

          {/* Frequency + time */}
          <div className="grid md:grid-cols-2 gap-4">
            <Select
              label={t('blogSeo.engine.frequencyLabel', 'Frequência')}
              options={FREQUENCY_OPTIONS}
              value={form.frequency}
              onChange={(e) => updateForm('frequency', e.target.value as FrequencyKey)}
            />
            <Input label={t('blogSeo.engine.timeLabel', 'Horário')} type="time" value={form.time_of_day} onChange={(e) => updateForm('time_of_day', e.target.value)} />
          </div>

          {/* Weekdays */}
          {showFormWeekdays && (
            <div>
              <label className="label">{t('blogSeo.engine.weekdaysLabel', 'Dias da semana')}</label>
              <div className="flex flex-wrap gap-2">
                {WEEKDAYS.map((d) => (
                  <button
                    key={d.value}
                    type="button"
                    onClick={() => toggleFormWeekday(d.value)}
                    className={
                      'w-12 py-2 rounded-lg text-sm font-medium border transition-all duration-200 ' +
                      (form.days_of_week.includes(d.value)
                        ? 'bg-gradient-to-r from-primary-600 to-purple-600 text-white border-transparent shadow'
                        : 'bg-white dark:bg-gray-800/50 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:border-primary-400')
                    }
                  >
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Posts per run + tone */}
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <label className="label">{t('blogSeo.engine.postsPerRunLabel', 'Posts por execução')}</label>
              <CountPills value={form.posts_per_run} onChange={(n) => updateForm('posts_per_run', n)} max={7} />
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                {t('blogSeo.engine.postsPerRunHelper', 'Quantos posts gerar por execução (ex.: 7 = um por dia na semana).')}
              </p>
            </div>
            <Select label={t('blogSeo.engine.toneLabel', 'Tom de voz')} options={TONE_OPTIONS} value={form.tone} onChange={(e) => updateForm('tone', e.target.value)} />
          </div>

          {/* Topics */}
          <Textarea
            label={t('blogSeo.engine.topicsLabel', 'Temas (um por linha)')}
            value={form.topics}
            onChange={(e) => updateForm('topics', e.target.value)}
            placeholder={t('blogSeo.engine.topicsPlaceholder', 'Deixe vazio para a IA sugerir a partir do seu negócio')}
          />

          {/* Blog connection */}
          <Select
            label={t('blogSeo.engine.publishTargetLabel', 'Destino de publicação')}
            options={connectionOptions}
            value={form.blog_connection_id}
            onChange={(e) => updateForm('blog_connection_id', e.target.value)}
          />

          {/* Ignore pattern + variation direction */}
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40 p-4">
            <VariationControls
              ignorePattern={form.ignore_pattern}
              onIgnoreChange={(v) => setForm((f) => ({ ...f, ignore_pattern: v }))}
              variationDirection={form.variation_direction}
              onVariationChange={(v) => setForm((f) => ({ ...f, variation_direction: v }))}
            />
          </div>

          {/* Auto publish toggle */}
          <div className="flex items-center justify-between gap-4 p-4 rounded-xl border border-gray-200 dark:border-gray-700 bg-gray-50/60 dark:bg-gray-800/40">
            <div>
              <p className="text-sm font-medium text-gray-900 dark:text-white">{t('blogSeo.engine.autoPublishLabel', 'Publicar automaticamente, sem revisão')}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400">{t('blogSeo.engine.autoPublishHint', 'Quando ativado, o conteúdo é publicado/enfileirado sem revisão manual.')}</p>
            </div>
            <Toggle checked={form.auto_publish} onChange={() => updateForm('auto_publish', !form.auto_publish)} />
          </div>

          {/* Note */}
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {t('blogSeo.engine.routineNote', 'Roda no dia escolhido e distribui os posts ao longo dos próximos dias.')}
          </p>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-2">
            <Button variant="ghost" onClick={() => setModalOpen(false)}>
              {t('blogSeo.engine.cancel', 'Cancelar')}
            </Button>
            <Button variant="primary" isLoading={savingSchedule} onClick={handleSaveSchedule}>
              {editingId ? t('blogSeo.engine.saveChanges', 'Salvar alterações') : t('blogSeo.engine.createRoutine', 'Criar rotina')}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default ContentEngine;
